import { Button, Pill, Tabs, Text, Tooltip } from '@innovationdepartment/proxima-ui';
import { ContentCopyOutlined, EditOutlined } from '@mui/icons-material';
import { useState } from 'react';
import SectionLayout from 'ui/SectionLayout';
import BackToAudiencesLink from '../../components/BackToAudiencesLink';
import DeleteAudienceModal from '../../components/DeleteAudienceModal';
import DeleteButton from '../../components/DeleteButton';
import {
  DefinitionFieldLabel,
  HeaderActions,
  TextInput,
  TwoColumns
} from '../../components/form.styles';
import PauseResumeButton from '../../components/PauseResumeButton';
import { PrimaryButton } from '../../components/shared.styles';
import { PAUSE_AVAILABLE } from '../../featureFlags';
import type { DerivedAudienceStatus } from '../../lib/deriveAudienceStatus';
import { audienceStatusPillVariant } from '../../lib/deriveAudienceStatus';
import { formatStampTime } from '../../lib/formatters';
import type { DeletePreviewState } from '../../store/customAudiencesSlice';
import type { AudienceDefinition, AudienceSnapshot } from '../../types';
import {
  EditActions,
  EditForm,
  HeaderDescription,
  HeaderSide,
  HeaderStamp,
  TabsWrapper,
  TitleStatus
} from './DefinitionDetails.styles';
import DefinitionTab from './DefinitionTab';
import DeliveriesTab, { type DeliveriesTabProps } from './DeliveriesTab';
import {
  ConfirmDiscardDelivery,
  DeliverHeaderActions,
  type DeliverHeaderActionsProps
} from './DeliveryPanel';
import SnapshotsTab from './SnapshotsTab';

export type DetailsTab = 'definition' | 'snapshots' | 'deliveries';

/** Which of the page's exclusive drafts is open: none, the edit form, or the Deliver-to panel. */
export type DetailsMode = 'idle' | 'editing' | 'delivering';

/** The Delete button and its dialog. */
export type DeleteActions = {
  /** Why Delete is disabled, or null when it is enabled. */
  blockedReason: string | null;
  dialogOpen: boolean;
  preview: DeletePreviewState | null;
  /** True while a delete runs. */
  deleting: boolean;
  onOpen: () => void;
  onRetryPreview: () => void;
  onConfirm: () => void;
  onClose: () => void;
};

const TABS: { label: string; key: DetailsTab }[] = [
  { label: 'Definition', key: 'definition' },
  { label: 'Snapshots', key: 'snapshots' },
  { label: 'Deliveries', key: 'deliveries' }
];

type DefinitionDetailsViewProps = {
  definition: AudienceDefinition | null;
  derived: DerivedAudienceStatus | null;
  /** Why the status is Unknown or only the last known one. */
  statusHoverText?: string;
  loading: boolean;
  error: string | null;
  viewerPath: string;
  activeTab: DetailsTab;
  mode: DetailsMode;
  /** True when leaving delivering mode would discard a picked account or ratio. */
  discardConfirm: boolean;
  updating: boolean;
  snapshots: AudienceSnapshot[];
  snapshotsError: string | null;
  snapshotsPageNumber: number;
  snapshotsPageCount: number;
  deliveries: DeliveriesTabProps;
  deliverActions: Omit<DeliverHeaderActionsProps, 'deleted' | 'confirmDiscard' | 'onCancel'>;
  deleteActions: DeleteActions;
  /** Opens the edit form, discarding any Deliver-to selection. */
  onStartEdit: () => void;
  /** Closes the edit form or the Deliver-to panel, discarding the panel's selection. */
  onCancelMode: () => void;
  onTabChange: (tab: DetailsTab) => void;
  onSnapshotsPageChange: (pageNumber: number) => void;
  onSetPaused: (paused: boolean) => void;
  onSaveDetails: (name: string, description: string) => Promise<boolean>;
  onClone: () => void;
  /** Shown while the page polls: when the details last finished loading, or null before the first load. */
  pollStatus: { lastCheckedAt: Date | null } | null;
};

const DefinitionDetailsView = (props: DefinitionDetailsViewProps) => {
  const { definition, derived, mode, deleteActions } = props;
  const deleted = definition?.status === 'deleted';
  const { submitting } = props.deliverActions;
  /*
   * Delivering mode puts the header in delivery mode on every tab, so a live draft stays visible.
   * The header then holds edit and the panel's actions, and edit asks before discarding a selection.
   */
  const delivering = mode === 'delivering';
  const editButton = deleted ? null : (
    <ConfirmDiscardDelivery
      confirm={props.discardConfirm}
      disabled={submitting}
      placement="bottom-left"
      onDiscard={props.onStartEdit}
      renderTrigger={onClick => (
        <Tooltip title="Edit name and description">
          <span>
            <Button
              label="Edit"
              size="small"
              variant="outlined"
              startIcon={<EditOutlined />}
              disabled={mode === 'editing' || submitting}
              onClick={onClick}
            />
          </span>
        </Tooltip>
      )}
    />
  );

  return (
    <SectionLayout
      title={definition?.name ?? (props.loading ? 'Loading…' : 'Custom audience')}
      aboveTitle={<BackToAudiencesLink to={props.viewerPath} />}
      titleAppendText={
        derived ? (
          <TitleStatus title={props.statusHoverText}>
            <Pill label={derived.status} variant={audienceStatusPillVariant(derived.status)} />
          </TitleStatus>
        ) : undefined
      }
      titleActions={
        definition ? (
          <HeaderSide>
            <HeaderActions role="group" aria-label="Page actions">
              {delivering ? (
                <>
                  {editButton}
                  <DeliverHeaderActions
                    deleted={deleted}
                    confirmDiscard={props.discardConfirm}
                    onCancel={props.onCancelMode}
                    {...props.deliverActions}
                  />
                </>
              ) : (
                <>
                  {deleted || !PAUSE_AVAILABLE ? null : (
                    <PauseResumeButton
                      audienceName={definition.name}
                      paused={definition.status === 'paused'}
                      disabled={props.updating}
                      onConfirm={() => props.onSetPaused(definition.status !== 'paused')}
                    />
                  )}
                  {deleted ? null : (
                    <DeleteButton
                      audienceName={definition.name}
                      blockedReason={deleteActions.blockedReason}
                      disabled={props.updating}
                      onClick={deleteActions.onOpen}
                    />
                  )}
                  {editButton}
                  <Button
                    label="Clone"
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyOutlined />}
                    onClick={props.onClone}
                  />
                </>
              )}
            </HeaderActions>
            {props.pollStatus ? (
              <HeaderStamp>
                <Text variant="body2" color="neutral50">
                  {props.pollStatus.lastCheckedAt
                    ? `Last checked: ${formatStampTime(props.pollStatus.lastCheckedAt)}`
                    : 'Checking…'}
                </Text>
              </HeaderStamp>
            ) : null}
          </HeaderSide>
        ) : undefined
      }
      headerContent={
        <>
          {definition?.description && mode !== 'editing' ? (
            <HeaderDescription>
              <Text variant="body2" color="neutral70">
                {definition.description}
              </Text>
            </HeaderDescription>
          ) : null}
          {definition && mode === 'editing' ? (
            <EditDetails
              definition={definition}
              saving={props.updating}
              onSave={props.onSaveDetails}
              onClose={props.onCancelMode}
            />
          ) : null}
          {definition ? (
            <TabsWrapper>
              <Tabs
                tabs={TABS}
                tab={props.activeTab}
                disable={submitting}
                onChange={(key: string) => props.onTabChange(key as DetailsTab)}
              />
            </TabsWrapper>
          ) : null}
        </>
      }
    >
      {props.error ? (
        <Text variant="body2" color="red60">
          Failed to load this audience: {props.error}
        </Text>
      ) : null}
      {definition && props.activeTab === 'definition' ? (
        <DefinitionTab definition={definition} errorMessage={derived?.errorMessage ?? null} />
      ) : null}
      {definition && props.activeTab === 'snapshots' ? (
        <SnapshotsTab
          snapshots={props.snapshots}
          snapshotsError={props.snapshotsError}
          snapshotsPageNumber={props.snapshotsPageNumber}
          snapshotsPageCount={props.snapshotsPageCount}
          onSnapshotsPageChange={props.onSnapshotsPageChange}
        />
      ) : null}
      {definition && props.activeTab === 'deliveries' ? (
        <DeliveriesTab {...props.deliveries} />
      ) : null}
      {definition ? (
        <DeleteAudienceModal
          open={deleteActions.dialogOpen}
          audienceName={definition.name}
          preview={deleteActions.preview}
          deleting={deleteActions.deleting}
          onRetryPreview={deleteActions.onRetryPreview}
          onConfirm={deleteActions.onConfirm}
          onClose={deleteActions.onClose}
        />
      ) : null}
    </SectionLayout>
  );
};

/* Name and description are the only fields a saved definition can change */
const EditDetails = ({
  definition,
  saving,
  onSave,
  onClose
}: {
  definition: AudienceDefinition;
  saving: boolean;
  onSave: (name: string, description: string) => Promise<boolean>;
  onClose: () => void;
}) => {
  const [name, setName] = useState(definition.name);
  const [description, setDescription] = useState(definition.description ?? '');
  return (
    <EditForm
      onSubmit={async event => {
        event.preventDefault();
        if (await onSave(name, description)) onClose();
      }}
    >
      <TwoColumns>
        <DefinitionFieldLabel>
          Name
          <TextInput value={name} onChange={event => setName(event.target.value)} autoFocus />
        </DefinitionFieldLabel>
        <DefinitionFieldLabel>
          Description
          <TextInput value={description} onChange={event => setDescription(event.target.value)} />
        </DefinitionFieldLabel>
      </TwoColumns>
      {/* Cancel is last, as it is in the page header */}
      <EditActions>
        <PrimaryButton
          label={saving ? 'Saving…' : 'Save'}
          size="small"
          submit
          disabled={saving || !name.trim()}
        />
        <Button label="Cancel" variant="outlined" size="small" onClick={onClose} />
      </EditActions>
    </EditForm>
  );
};

export default DefinitionDetailsView;
